/**
 * Autocomplete.
 *
 * The hard constraint here is that most of a FlowScript document is *prose*.
 * A completion popup that fires while someone is typing "Send the confirmation
 * email" would be intolerable, so the source returns `null` unless the cursor
 * is somewhere a keyword could legally start: the beginning of a statement,
 * after `@`, or after `call`.
 */
import { snippetCompletion } from '@codemirror/autocomplete';
import type {
  Completion,
  CompletionContext,
  CompletionResult,
  CompletionSource,
} from '@codemirror/autocomplete';
import { STATEMENT_KEYWORDS } from '../language/tokenizer/tokens';
import { ANNOTATION_NAMES, ICON_SLUGS } from '../language/parser/metadata';

/** Snippet bodies use the canonical 4-space Allman style the formatter emits. */
const SNIPPETS: readonly Completion[] = [
  snippetCompletion('if (${condition})\n{\n    ${body}\n}', {
    label: 'if',
    detail: 'decision',
    type: 'keyword',
    boost: 60,
  }),
  snippetCompletion('if (${condition})\n{\n    ${then}\n}\nelse\n{\n    ${otherwise}\n}', {
    label: 'ifelse',
    displayLabel: 'if / else',
    detail: 'decision with both branches',
    type: 'keyword',
    boost: 59,
  }),
  snippetCompletion('else if (${condition})\n{\n    ${body}\n}', {
    label: 'elseif',
    displayLabel: 'else if',
    detail: 'chained decision',
    type: 'keyword',
  }),
  snippetCompletion('while (${condition})\n{\n    ${body}\n}', {
    label: 'while',
    detail: 'loop while true',
    type: 'keyword',
    boost: 50,
  }),
  snippetCompletion('repeat\n{\n    ${body}\n} until (${condition});', {
    label: 'repeat',
    displayLabel: 'repeat / until',
    detail: 'loop at least once',
    type: 'keyword',
  }),
  snippetCompletion('for each (${item} in ${collection})\n{\n    ${body}\n}', {
    label: 'foreach',
    displayLabel: 'for each',
    detail: 'loop over a collection',
    type: 'keyword',
  }),
  snippetCompletion(
    'switch (${subject})\n{\n    case "${first}":\n    {\n        ${a}\n    }\n\n    case "${second}":\n    {\n        ${b}\n    }\n\n    default:\n    {\n        ${fallback}\n    }\n}',
    { label: 'switch', detail: 'multi-way decision', type: 'keyword' },
  ),
  snippetCompletion('function ${name}\n{\n    ${body}\n}', {
    label: 'function',
    detail: 'reusable sub-flow',
    type: 'keyword',
    boost: 40,
  }),
  snippetCompletion('call ${name};', {
    label: 'call',
    detail: 'run a function',
    type: 'keyword',
    boost: 45,
  }),
  snippetCompletion('start ${label};', {
    label: 'start',
    detail: 'entry terminator',
    type: 'keyword',
  }),
  snippetCompletion('end ${label};', { label: 'end', detail: 'exit terminator', type: 'keyword' }),
  snippetCompletion('return ${value};', {
    label: 'return',
    detail: 'leave the function',
    type: 'keyword',
  }),
  snippetCompletion('break;', { label: 'break', detail: 'leave the loop', type: 'keyword' }),
  snippetCompletion('continue;', { label: 'continue', detail: 'next iteration', type: 'keyword' }),
  snippetCompletion('@color("${#2563eb}")\n', {
    label: '@color',
    detail: 'node colour',
    type: 'property',
  }),
  snippetCompletion('@icon("${user}")\n', {
    label: '@icon',
    detail: 'node icon',
    type: 'property',
  }),
  snippetCompletion('@note("${text}")\n', {
    label: '@note',
    detail: 'tooltip note',
    type: 'property',
  }),
];

const KEYWORD_COMPLETIONS: readonly Completion[] = [...STATEMENT_KEYWORDS]
  .sort()
  .map((keyword) => ({ label: keyword, type: 'keyword' as const, boost: -10 }));

const ANNOTATION_COMPLETIONS: readonly Completion[] = ANNOTATION_NAMES.map((name) => ({
  label: name,
  type: 'property' as const,
  detail: 'annotation',
}));

const ICON_COMPLETIONS: readonly Completion[] = ICON_SLUGS.map((slug) => ({
  label: slug,
  type: 'enum' as const,
}));

/**
 * Everything before the cursor on the current line. Cheap enough to recompute
 * per keystroke and far simpler than threading parser state in here.
 */
function lineBefore(context: CompletionContext): string {
  const line = context.state.doc.lineAt(context.pos);
  return context.state.sliceDoc(line.from, context.pos);
}

export function flowScriptCompletions(getFunctionNames: () => readonly string[]): CompletionSource {
  return (context: CompletionContext): CompletionResult | null => {
    const before = lineBefore(context);

    // Inside `@icon("…` — offer the closed set of icon slugs.
    const iconMatch = /@icon\(\s*"([\w-]*)$/.exec(before);
    if (iconMatch) {
      return {
        from: context.pos - (iconMatch[1]?.length ?? 0),
        options: [...ICON_COMPLETIONS],
        validFor: /^[\w-]*$/,
      };
    }

    // Right after `@` — annotation names.
    const annotationMatch = /@([\w-]*)$/.exec(before);
    if (annotationMatch) {
      return {
        from: context.pos - (annotationMatch[1]?.length ?? 0),
        options: [...ANNOTATION_COMPLETIONS],
        validFor: /^[\w-]*$/,
      };
    }

    // After `call ` — declared function names, and nothing else.
    const callMatch = /(?:^|[;{}])\s*call\s+([\w-]*)$/i.exec(before);
    if (callMatch) {
      const names = getFunctionNames();
      if (names.length === 0) return null;
      return {
        from: context.pos - (callMatch[1]?.length ?? 0),
        options: names.map((name) => ({
          label: name,
          type: 'function',
          detail: 'declared function',
        })),
        validFor: /^[\w-]*$/,
      };
    }

    // Statement start — keywords and snippets. Anywhere else, stay silent so
    // ordinary prose is never interrupted.
    const startMatch = /(?:^|[;{}])\s*(\w*)$/.exec(before);
    if (!startMatch) return context.explicit ? explicitFallback(context) : null;

    const typed = startMatch[1] ?? '';
    if (typed.length === 0 && !context.explicit) return null;

    return {
      from: context.pos - typed.length,
      options: [...SNIPPETS, ...KEYWORD_COMPLETIONS],
      validFor: /^\w*$/,
    };
  };
}

/** Ctrl-Space anywhere still offers the full set rather than nothing. */
function explicitFallback(context: CompletionContext): CompletionResult {
  const word = context.matchBefore(/\w*/);
  return {
    from: word ? word.from : context.pos,
    options: [...SNIPPETS, ...KEYWORD_COMPLETIONS],
    validFor: /^\w*$/,
  };
}
